import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import TherapistList from '@/components/TherapistList';
import { translations, type Language } from '@/i18n/translations';

// Map URL paths to language codes
const pathToLang: Record<string, Language> = {
  '/en': 'EN',
  '/kor': 'KOR',
  '/ch': 'CHN',
  '/': 'EN',
};

const langToPath: Record<Language, string> = {
  'EN': '/en',
  'KOR': '/kor',
  'CHN': '/ch',
};

export default function HomePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<string | null>(null);
  const [isCtaOpen, setIsCtaOpen] = useState(false);

  // Get initial language from URL path
  const getInitialLang = (): Language => {
    const path = location.pathname.replace(/\/$/, ''); // Remove trailing slash
    return pathToLang[path] || 'EN';
  };

  const [lang, setLang] = useState<Language>(getInitialLang);

  const t = translations[lang];

  // Update language when URL changes
  useEffect(() => {
    const path = location.pathname.replace(/\/$/, '');
    const urlLang = pathToLang[path];
    if (urlLang && urlLang !== lang) {
      setLang(urlLang);
    }
  }, [location.pathname]);

  // Handle language change - update URL
  const handleLangChange = (newLang: Language) => {
    setLang(newLang);
    navigate(langToPath[newLang]);
  };

  // Update document meta tags on language change
  useEffect(() => {
    const m = t.meta;
    document.documentElement.lang = m.lang;
    document.title = m.title;
    const setMeta = (sel: string, val: string) => {
      const el = document.querySelector(sel);
      if (el) el.setAttribute('content', val);
    };
    setMeta('meta[name="description"]', m.description);
    setMeta('meta[property="og:title"]', m.ogTitle);
    setMeta('meta[property="og:description"]', m.ogDescription);
    setMeta('meta[name="twitter:title"]', m.ogTitle);
    setMeta('meta[name="twitter:description"]', m.ogDescription);
  }, [lang]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white/10 backdrop-blur-xl'
        }`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="https://static.readdy.ai/image/38c616fd8ccf1a8e3e37c843eeb88e31/7e733299bbff473d8f94beaff4f3770b.png"
              alt="Sakura-an Logo"
              className="hidden md:block h-12 w-auto object-contain"
            />
            <div className={`text-xl md:text-2xl font-serif ${isScrolled ? 'text-gray-800' : 'text-white'}`}>
              Sakura-an <span className="text-sm md:text-lg">咲くら庵</span>
            </div>
          </div>

          <div className="hidden lg:flex items-center gap-12">
            <button onClick={() => scrollToSection('concept')} className={`text-sm font-light hover:text-[#FFB7C5] transition-colors ${isScrolled ? 'text-gray-800' : 'text-white'}`}>{t.nav.experience}</button>
            <button onClick={() => scrollToSection('menu')} className={`text-sm font-light hover:text-[#FFB7C5] transition-colors ${isScrolled ? 'text-gray-800' : 'text-white'}`}>{t.nav.menu}</button>
            <button onClick={() => scrollToSection('therapists')} className={`text-sm font-light hover:text-[#FFB7C5] transition-colors ${isScrolled ? 'text-gray-800' : 'text-white'}`}>{t.nav.therapists}</button>
            <button onClick={() => scrollToSection('access')} className={`text-sm font-light hover:text-[#FFB7C5] transition-colors ${isScrolled ? 'text-gray-800' : 'text-white'}`}>{t.nav.access}</button>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1 md:gap-2 bg-gray-100/80 backdrop-blur rounded-full px-2 md:px-3 py-1.5 border border-gray-200">
              <i className="ri-global-line text-gray-500 text-sm md:text-base"></i>
              {(['EN', 'KOR', 'CHN'] as Language[]).map((l) => (
                <button
                  key={l}
                  onClick={() => handleLangChange(l)}
                  className={`text-[10px] md:text-xs font-medium px-1.5 md:px-2 py-0.5 rounded-full transition-all ${lang === l ? 'bg-[#FFB7C5] text-white shadow-sm' : 'text-gray-500 hover:text-gray-800'
                    }`}
                >
                  {l}
                </button>
              ))}
            </div>

            <button
              onClick={() => scrollToSection('booking')}
              className="hidden md:inline-block bg-[#FFB7C5] text-white px-4 md:px-6 py-2 md:py-2.5 rounded-full text-xs md:text-sm font-medium hover:bg-[#ff9fb4] transition-all hover:shadow-lg whitespace-nowrap cursor-pointer"
            >
              {t.nav.bookNow}
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative h-[90vh] flex items-end justify-center overflow-hidden pb-16 md:pb-24">
        <div className="absolute inset-0">
          <img
            src="/sakuran-en_LP/hero.jpg"
            alt="Sakura-an Spa Interior"
            className="w-full h-full object-cover object-[center_30%]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/60"></div>
        </div>

        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <div className="inline-block border border-white/80 rounded-full px-5 py-2 mb-6 backdrop-blur-sm">
            <span className="text-white text-xs uppercase tracking-wider">{t.hero.tag}</span>
          </div>

          <h1 className="text-4xl md:text-7xl font-bold text-white mb-6 leading-tight tracking-tight">
            {t.hero.title}
          </h1>

          <p className="text-lg md:text-xl text-white/90 mb-8 font-light">
            {t.hero.subtitle}
          </p>

          <div className="flex flex-row items-center justify-center gap-4">
            <button
              onClick={() => scrollToSection('booking')}
              className="bg-[#FFB7C5] text-white px-6 md:px-8 py-3 md:py-4 rounded-full text-sm md:text-base font-medium hover:bg-[#ff9fb4] transition-all hover:shadow-2xl whitespace-nowrap cursor-pointer"
            >
              {t.hero.bookNow}
            </button>
            <button
              onClick={() => scrollToSection('menu')}
              className="border-2 border-white text-white px-6 md:px-8 py-3 md:py-4 rounded-full text-sm md:text-base font-medium hover:bg-white hover:text-gray-800 transition-all whitespace-nowrap cursor-pointer"
            >
              {t.hero.viewMenu}
            </button>
          </div>
        </div>
      </section>

      {/* Disclaimer Banner */}
      <div className="bg-[#2C2C2C] py-3 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-3">
          <i className="ri-shield-star-line text-[#FFB7C5] text-lg"></i>
          <p className="text-white text-sm font-medium tracking-wide">
            {t.disclaimer.line1}
            <span className="mx-2 text-gray-500">|</span>
            {t.disclaimer.line2}
          </p>
        </div>
      </div>

      {/* Concept Section */}
      <section id="concept" className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <div className="text-[#FFB7C5] text-xs uppercase tracking-widest mb-3">{t.concept.tag}</div>
            <h2 className="text-5xl md:text-6xl font-bold text-gray-800 mb-4">{t.concept.title}</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">{t.concept.subtitle}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-[#FAF8F5] rounded-3xl p-10 hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 flex items-center justify-center mb-6">
                <i className="ri-door-open-line text-5xl text-[#FFB7C5]"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-3">{t.concept.rooms.title}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t.concept.rooms.desc}
              </p>
            </div>

            <div className="bg-[#FAF8F5] rounded-3xl p-10 hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 flex items-center justify-center mb-6">
                <i className="ri-user-heart-line text-5xl text-[#FFB7C5]"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-3">{t.concept.staff.title}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t.concept.staff.desc}
              </p>
            </div>

            <div className="bg-[#FAF8F5] rounded-3xl p-10 hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 flex items-center justify-center mb-6">
                <i className="ri-flower-line text-5xl text-[#FFB7C5]"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-3">{t.concept.atmosphere.title}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t.concept.atmosphere.desc}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-6">
            {t.concept.features.map((feature: string, idx: number) => (
              <div key={idx} className="border border-[#FFB7C5] rounded-full px-6 py-3 bg-white">
                <span className="text-sm text-gray-700">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Menu & Pricing Section */}
      <section id="menu" className="py-24 px-6 bg-[#2C2C2C] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }}></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <div className="text-[#FFB7C5] text-xs uppercase tracking-widest mb-3">{t.menu.tag}</div>
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-5">{t.menu.title}</h2>
            <p className="text-lg text-gray-400">{t.menu.subtitle}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {/* 90 min Course */}
            <div className="bg-[#1A1A1A] rounded-2xl p-8 md:p-12 hover:shadow-2xl transition-all flex flex-col">
              <div className="text-5xl md:text-6xl font-bold text-white mb-2">{t.menu.courses.c90.title}</div>
              <div className="text-lg md:text-xl text-gray-400 mb-6">{t.menu.courses.c90.service}</div>

              <div className="mb-8">
                <span className="text-5xl font-bold text-white">¥15,000</span>
              </div>

              <ul className="space-y-3 mb-8">
                {t.menu.courses.c90.features.map((f: string, i: number) => (
                  <li key={i} className="flex items-start gap-3 text-white">
                    <i className="ri-check-line text-[#FFB7C5] text-xl mt-0.5"></i>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => {
                  setSelectedCourse('90min');
                  scrollToSection('booking');
                }}
                className="w-full bg-[#FFB7C5] text-white py-4 rounded-lg font-medium hover:bg-[#ff9fb4] transition-all hover:-translate-y-1 whitespace-nowrap cursor-pointer mt-auto"
              >
                {t.menu.select}
              </button>
            </div>

            {/* 100 min Course - Featured */}
            <div className="bg-[#1A1A1A] rounded-2xl p-8 md:p-12 hover:shadow-2xl transition-all md:-translate-y-2 border-2 border-[#FFB7C5] flex flex-col">
              <div className="inline-block bg-[#FFB7C5] text-white text-xs px-3 py-1 rounded-full mb-4">{t.menu.popular}</div>
              <div className="text-5xl md:text-6xl font-bold text-white mb-2">{t.menu.courses.c100.title}</div>
              <div className="text-lg md:text-xl text-gray-400 mb-6">{t.menu.courses.c100.service}</div>

              <div className="mb-8">
                <span className="text-5xl font-bold text-white">¥17,000</span>
              </div>

              <ul className="space-y-3 mb-8">
                {t.menu.courses.c100.features.map((f: string, i: number) => (
                  <li key={i} className="flex items-start gap-3 text-white">
                    <i className="ri-check-line text-[#FFB7C5] text-xl mt-0.5"></i>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => {
                  setSelectedCourse('100min');
                  scrollToSection('booking');
                }}
                className="w-full bg-[#FFB7C5] text-white py-4 rounded-lg font-medium hover:bg-[#ff9fb4] transition-all hover:-translate-y-1 whitespace-nowrap cursor-pointer mt-auto"
              >
                {t.menu.select}
              </button>
            </div>

            {/* 120 min Course */}
            <div className="bg-[#1A1A1A] rounded-2xl p-8 md:p-12 hover:shadow-2xl transition-all flex flex-col">
              <div className="text-5xl md:text-6xl font-bold text-white mb-2">{t.menu.courses.c120.title}</div>
              <div className="text-lg md:text-xl text-gray-400 mb-6">{t.menu.courses.c120.service}</div>

              <div className="mb-8">
                <span className="text-5xl font-bold text-white">¥20,000</span>
              </div>

              <ul className="space-y-3 mb-8">
                {t.menu.courses.c120.features.map((f: string, i: number) => (
                  <li key={i} className="flex items-start gap-3 text-white">
                    <i className="ri-check-line text-[#FFB7C5] text-xl mt-0.5"></i>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => {
                  setSelectedCourse('120min');
                  scrollToSection('booking');
                }}
                className="w-full bg-[#FFB7C5] text-white py-4 rounded-lg font-medium hover:bg-[#ff9fb4] transition-all hover:-translate-y-1 whitespace-nowrap cursor-pointer mt-auto"
              >
                {t.menu.select}
              </button>
            </div>
          </div>

        </div>
      </section>

      {/* Therapists Section */}
      <section id="therapists" className="py-24 px-6 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <h2 className="text-5xl font-bold text-gray-800 mb-4">{t.therapists.title}</h2>
            <p className="text-lg text-gray-600">{t.therapists.subtitle}</p>
            <p className="text-sm text-[#FFB7C5] mt-2 font-medium">{t.therapists.nominationFee}</p>
          </div>

          <div className="flex gap-5 overflow-x-auto pb-6 scrollbar-hide snap-x snap-mandatory" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <TherapistList lang={lang} />
          </div>
        </div>
      </section>

      {/* Location & Access Section */}
      <section id="access" className="grid md:grid-cols-5 gap-0">
        <div className="md:col-span-3 h-[600px] relative border-r-2 border-[#FFB7C5]">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3281.307613!2d135.5008667!3d34.6673384!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6000e71c95c5d59f%3A0x58c756ce51ebc7c2!2zTWVuJ3MgU1BBIFNha3VyYS1hbg!5e0!3m2!1sen!2sjp!4v1737302400000!5m2!1sen!2sjp"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            title="Sakura-an Location"
          ></iframe>
        </div>

        <div className="md:col-span-2 bg-[#FAF8F5] p-16 flex flex-col justify-center">
          <div className="text-[#FFB7C5] text-xs uppercase tracking-widest mb-3">{t.access.tag}</div>
          <h2 className="text-4xl font-bold text-gray-800 mb-6">{t.access.title}</h2>

          <div className="mb-8">
            <div className="flex items-start gap-3 mb-2">
              <i className="ri-map-pin-line text-[#FFB7C5] text-2xl"></i>
              <div>
                <p className="text-lg text-gray-800 font-medium">4F, 1-9-12 Sennichimae, Chuo-ku, Osaka</p>
                <p className="text-gray-600">542-0074</p>
              </div>
            </div>
          </div>

          <div className="space-y-4 mb-10">
            <div className="flex items-center gap-3 text-gray-700">
              <i className="ri-subway-line text-xl text-[#FFB7C5]"></i>
              <span>{t.access.namba}</span>
            </div>
            <div className="flex items-center gap-3 text-gray-700">
              <i className="ri-subway-line text-xl text-[#FFB7C5]"></i>
              <span>{t.access.nipponbashi}</span>
            </div>
            <div className="flex items-center gap-3 text-gray-700">
              <i className="ri-store-2-line text-xl text-[#FFB7C5]"></i>
              <span>{t.access.dotonbori}</span>
            </div>
          </div>

          <a href="https://maps.app.goo.gl/HQKVJaJusEnZSc7SA" target="_blank" rel="noopener noreferrer" className="block w-full bg-[#FFB7C5] text-white py-4 rounded-lg font-medium hover:bg-[#ff9fb4] transition-all text-lg whitespace-nowrap cursor-pointer text-center">
            {t.access.openInMaps}
          </a>
        </div>
      </section>

      {/* Final CTA Section */}
      <section id="booking" className="py-32 px-6 bg-gradient-to-b from-[#FFE5EC] to-white relative overflow-hidden">
        <div className="absolute top-10 left-10 opacity-5">
          <i className="ri-flower-line text-[200px] text-[#FFB7C5]"></i>
        </div>
        <div className="absolute bottom-10 right-10 opacity-5">
          <i className="ri-flower-line text-[200px] text-[#FFB7C5]"></i>
        </div>

        <div className="max-w-3xl mx-auto text-center relative z-10">
          <h2 className="text-5xl md:text-6xl font-bold text-gray-800 mb-5">{t.booking.title}</h2>
          <p className="text-xl text-gray-600 mb-12">{t.booking.subtitle}</p>

          <div className="flex flex-wrap justify-center gap-6 mb-12">
            <a href="https://line.me/ti/p/SLGJSTrCeD" target="_blank" rel="noreferrer" className="transform hover:scale-105 transition-transform rounded-2xl overflow-hidden w-14 h-14 md:w-16 md:h-16"><img src="/sakuran-en_LP/cta/line_v2.png" alt="LINE" className="w-full h-full object-cover scale-110" /></a>
            <a href="https://open.kakao.com/o/sAB3cZhh" target="_blank" rel="noreferrer" className="transform hover:scale-105 transition-transform rounded-2xl overflow-hidden w-14 h-14 md:w-16 md:h-16"><img src="/sakuran-en_LP/cta/kakao_v2.png" alt="Kakao" className="w-full h-full object-cover" /></a>
            <a href="https://api.whatsapp.com/message/KUINDFV7EKVRH1?autoload=1&app_absent=0" target="_blank" rel="noreferrer" className="transform hover:scale-105 transition-transform rounded-2xl overflow-hidden w-14 h-14 md:w-16 md:h-16"><img src="/sakuran-en_LP/cta/whatsapp_v2.png" alt="WhatsApp" className="w-full h-full object-cover" /></a>
            <div className="flex flex-col items-center transform hover:scale-105 transition-transform">
              <div className="rounded-2xl overflow-hidden w-14 h-14 md:w-16 md:h-16"><img src="/sakuran-en_LP/cta/wechat_v2.png" alt="WeChat" className="w-full h-full object-cover" /></div>
              <span className="text-[10px] md:text-xs mt-1 font-bold text-gray-600">ID: SakuraanSpa</span>
            </div>
          </div>



          <div className="flex flex-wrap items-center justify-center gap-8 mt-12">
            <div className="flex items-center gap-2">
              <i className="ri-shield-check-line text-2xl text-[#FFB7C5]"></i>
              <span className="text-sm text-gray-600">{t.booking.secure}</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-time-line text-2xl text-[#FFB7C5]"></i>
              <span className="text-sm text-gray-600">{t.booking.instant}</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-chat-smile-3-line text-2xl text-[#FFB7C5]"></i>
              <span className="text-sm text-gray-600">{t.booking.support}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#2C2C2C] rounded-t-3xl mx-6 mb-6 px-16 py-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-10 gap-12 mb-12">
            {/* Brand Column */}
            <div className="md:col-span-4">
              <div className="text-3xl font-serif text-white mb-4">Sakura-an <span className="text-xl">咲くら庵</span></div>
              <p className="text-gray-400 mb-8">{t.footer.tag}</p>
            </div>

            {/* Quick Links */}
            <div className="md:col-span-3">
              <h4 className="text-sm uppercase text-gray-500 tracking-wider mb-5">{t.footer.quickLinks}</h4>
              <ul className="space-y-3">
                <li><button onClick={() => scrollToSection('concept')} className="text-white hover:text-[#FFB7C5] transition-colors cursor-pointer">{t.nav.experience}</button></li>
                <li><button onClick={() => scrollToSection('menu')} className="text-white hover:text-[#FFB7C5] transition-colors cursor-pointer">{t.nav.menu}</button></li>
                <li><button onClick={() => scrollToSection('therapists')} className="text-white hover:text-[#FFB7C5] transition-colors cursor-pointer">{t.nav.therapists}</button></li>
                <li><button onClick={() => scrollToSection('access')} className="text-white hover:text-[#FFB7C5] transition-colors cursor-pointer">{t.nav.access}</button></li>
                <li><a href="#faq" className="text-white hover:text-[#FFB7C5] transition-colors cursor-pointer">FAQ</a></li>
              </ul>
            </div>

            {/* Contact */}
            <div className="md:col-span-3">
              <h4 className="text-sm uppercase text-gray-500 tracking-wider mb-5">{t.footer.contact}</h4>
              <div className="space-y-3">
                <a href="tel:0662118873" className="text-lg text-white hover:text-[#FFB7C5] transition-colors block cursor-pointer">06-6211-8873</a>

                <div className="flex gap-4 mt-4">
                  <a href="https://line.me/ti/p/SLGJSTrCeD" target="_blank" rel="noreferrer"><i className="ri-line-line text-2xl text-white hover:text-[#00B900]"></i></a>
                  <a href="https://api.whatsapp.com/message/KUINDFV7EKVRH1?autoload=1&app_absent=0" target="_blank" rel="noreferrer"><i className="ri-whatsapp-line text-2xl text-white hover:text-[#25D366]"></i></a>
                </div>

                <div className="mt-6 pt-6 border-t border-white/10">
                  <p className="text-white font-medium">{t.footer.openDaily}</p>
                  <p className="text-[#FFB7C5] font-bold text-lg">11:00 - 25:00</p>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-500">© 2025 Sakura-an. All rights reserved.</p>
          </div>
        </div>
      </footer>
      {/* Floating CTA */}
      <div className="fixed bottom-6 right-6 z-[60] flex flex-col items-end gap-3">
        {isCtaOpen && (
          <div className="flex flex-col gap-4 mb-3 animate-in fade-in slide-in-from-bottom-4 duration-300">
            <a href="https://line.me/ti/p/SLGJSTrCeD" target="_blank" rel="noreferrer"
              className="hover:scale-110 transition-transform rounded-2xl overflow-hidden w-14 h-14 md:w-16 md:h-16">
              <img src="/sakuran-en_LP/cta/line_v2.png" alt="LINE Booking" className="w-full h-full object-cover scale-110" />
            </a>
            <a href="https://open.kakao.com/o/sAB3cZhh" target="_blank" rel="noreferrer"
              className="hover:scale-110 transition-transform rounded-2xl overflow-hidden w-14 h-14 md:w-16 md:h-16">
              <img src="/sakuran-en_LP/cta/kakao_v2.png" alt="KakaoTalk Booking" className="w-full h-full object-cover" />
            </a>
            <a href="https://api.whatsapp.com/message/KUINDFV7EKVRH1?autoload=1&app_absent=0" target="_blank" rel="noreferrer"
              className="hover:scale-110 transition-transform rounded-2xl overflow-hidden w-14 h-14 md:w-16 md:h-16">
              <img src="/sakuran-en_LP/cta/whatsapp_v2.png" alt="WhatsApp Booking" className="w-full h-full object-cover" />
            </a>
            <div className="group relative">
              <div className="hover:scale-110 transition-transform rounded-2xl overflow-hidden w-14 h-14 md:w-16 md:h-16">
                <img src="/sakuran-en_LP/cta/wechat_v2.png" alt="WeChat" className="w-full h-full object-cover" />
              </div>
              <div className="absolute right-full mr-4 top-1/2 -translate-y-1/2 bg-white/95 backdrop-blur px-3 py-2 rounded-xl shadow-lg border border-pink-100 whitespace-nowrap animate-in fade-in slide-in-from-right-2">
                <p className="text-[10px] text-pink-400 font-bold uppercase tracking-widest mb-0.5">WeChat ID</p>
                <p className="text-sm font-bold text-gray-800">SakuraanSpa</p>
              </div>
            </div>
          </div>
        )}
        <button
          onClick={() => setIsCtaOpen(!isCtaOpen)}
          className={`bg-[#FFB7C5] text-white w-14 h-14 md:w-16 md:h-16 rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 transform active:scale-95 z-10 ${isCtaOpen ? 'rotate-90' : ''}`}
        >
          {isCtaOpen ? (
            <i className="ri-close-line text-2xl md:text-3xl font-bold"></i>
          ) : (
            <div className="flex flex-col items-center">
              <i className="ri-chat-3-line text-2xl md:text-3xl mb-[-4px]"></i>
              <span className="text-[9px] md:text-[10px] font-bold uppercase tracking-tight">Book</span>
            </div>
          )}
        </button>
      </div>
    </div>
  );
}
